import { z } from "zod";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { OccurrenceAmenitiesSchema, OccurrenceAmenityClashSchema, type OccurrenceAmenities, type OccurrenceAmenityClash } from "@/types/OccurrenceAmenities";
import { ApiError } from "./ApiError";
import { API_BASE_URL } from "./helpers";
import type { IOccurrenceAmenitiesAPI } from "./occurrenceAmenitiesApi";

/** A rejected per-date change: `message` is the server's human `details`; clashes render inline. */
export class OccurrenceAmenitiesError extends ApiError {
  readonly details: string;
  readonly clashes: OccurrenceAmenityClash[];
  readonly invalidDates: string[];

  constructor(status: number, details: string, clashes: OccurrenceAmenityClash[] = [], invalidDates: string[] = []) {
    super(status, details);
    this.name = "OccurrenceAmenitiesError";
    this.details = details;
    this.clashes = clashes;
    this.invalidDates = invalidDates;
  }
}

const url = (bookingId: string) => `${API_BASE_URL}/api/booking/${bookingId}/occurrence-amenities`;

async function failure(response: Response): Promise<never> {
  const body = await response.json().catch(() => ({}));
  const clashes = z.array(OccurrenceAmenityClashSchema).safeParse(body?.clashes);
  throw new OccurrenceAmenitiesError(
    response.status,
    body?.details || body?.error || `API error: ${response.status}`,
    clashes.success ? clashes.data : [],
    Array.isArray(body?.invalidDates) ? body.invalidDates : [],
  );
}

export default class RemoteOccurrenceAmenitiesAPI implements IOccurrenceAmenitiesAPI {
  async fetchOccurrenceAmenities(bookingId: string): Promise<OccurrenceAmenities[]> {
    const response = await fetchWithAuth(url(bookingId), { method: "GET", credentials: "include" });
    if (!response.ok) return failure(response);
    const body = await response.json();
    return z.array(OccurrenceAmenitiesSchema).parse(body.data);
  }

  async setOccurrenceAmenities(bookingId: string, dates: string[], amenityIds: string[]): Promise<void> {
    const response = await fetchWithAuth(url(bookingId), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ dates, amenityIds }),
    });
    if (!response.ok) return failure(response);
  }

  async resetOccurrenceAmenities(bookingId: string, dates: string[]): Promise<void> {
    const response = await fetchWithAuth(url(bookingId), {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ dates }),
    });
    if (!response.ok) return failure(response);
  }
}
